import { LegacySymptomId } from "@qendo/trackables";
import { type Href, router } from "expo-router";
import { useState } from "react";
import { View } from "react-native";
import type { NativeSyntheticEvent } from "react-native/Libraries/Types/CoreEventTypes";
import { Button } from "~/components/Button";
import { ListingGroup } from "~/components/layout/ListingGroup";
import { ListingItem } from "~/components/layout/ListingItem";
import { FullPageModal, type FullPageModalProps } from "~/components/layout/modal/FullPageModal";
import { BaseText, Title } from "~/components/layout/text";
import { Only } from "~/components/permission/Only";
import { QendoIcon } from "~/components/QendoIcon";
import { Flex, Gap, Items, Padding } from "~/constants/layout";
import { useDismiss } from "~/providers/DismissProvider";
import { useTheme } from "~/providers/ThemeProvider";
import { itemWidth, setWidthOnLayout } from "~/util/layout";

export type FlareProps = Omit<FullPageModalProps, "cancelable" | "withDone" | "children">;

export const Flare = ({ onRequestClose, asRoute, ...modalProps }: FlareProps) => {
    const { colours } = useTheme();
    const [active, setActive] = useState(false);
    const [width, setWidth] = useState(300);
    const handleOnLayout = setWidthOnLayout(setWidth);
    const buttonWidth = itemWidth(width, 2);
    const { handleDismiss } = useDismiss();

    const dismissWith = (event: NativeSyntheticEvent<unknown>) => {
        if (onRequestClose) {
            onRequestClose(event);
        } else if (asRoute) {
            handleDismiss();
        }
    };

    const navigateTo = (href: Href, event: NativeSyntheticEvent<unknown>) => {
        if (asRoute) {
            router.replace(href);
        } else {
            router.navigate(href);
            dismissWith(event);
        }
    };

    const log_flare: Href = { pathname: "/journal/[id]/create", params: { id: LegacySymptomId.flare } };

    return (
        <FullPageModal
            {...modalProps}
            asRoute={asRoute}
            cancelable={!active}
            withDone={active}
            onRequestClose={dismissWith}
        >
            <Only when={!active}>
                <View style={[Padding.vertical_12, Padding.horizontal_lg, Gap.sm, Items.center]}>
                    <QendoIcon name="zap" color={colours.icon.danger} size={48} />
                    <BaseText align="center">Are you having a flare right now?</BaseText>
                    <View style={[Flex.row, Gap.sm, Items.center]} onLayout={handleOnLayout}>
                        <Button
                            title="Yes"
                            variant="primary"
                            style={{ width: buttonWidth }}
                            onPress={() => setActive(true)}
                        />
                        <Button
                            title="No"
                            variant="secondary"
                            style={{ width: buttonWidth }}
                            onPress={(e) => navigateTo(log_flare, e)}
                        />
                    </View>
                </View>
            </Only>
            <Only when={active}>
                <Title>Manage your flare</Title>
                <ListingGroup>
                    <ListingItem
                        icon="prescribed-file"
                        title="Prescibed pain plan"
                        onPress={(e) => navigateTo("/health-profile/pain-plan", e)}
                        hideChevron
                    />
                    <ListingItem
                        icon="resources"
                        title="QENDO resources"
                        onPress={(e) => navigateTo("/resources", e)}
                        hideChevron
                    />
                    <ListingItem
                        icon="care-team"
                        title="Care team"
                        onPress={(e) => navigateTo("/more/care-team", e)}
                        hideChevron
                        last
                    />
                </ListingGroup>
                <ListingGroup>
                    <ListingItem
                        icon="zap"
                        title="Log flare details"
                        onPress={(e) => navigateTo(log_flare, e)}
                        hideChevron
                    />
                </ListingGroup>
            </Only>
        </FullPageModal>
    );
};
